import { isSupportedLocale, SOURCE_LOCALE, type SupportedLocale } from "~/config/i18n";
import { type AppDensity, DEFAULT_APP_DENSITY, isAppDensity } from "~/lib/density";

const STORAGE_KEY = "user-preferences";

export type ThemeMode = "system" | "light" | "dark";

const VALID_THEMES = new Set<string>(["system", "light", "dark"]);

export interface UserPreferences {
  /** Show the "Edit Dashboard" button on the dashboard header. */
  showDashboardEditButton: boolean;
  /**
   * Theme mode: force light (the default), force dark, or follow the OS.
   *
   * The default is `light`, not `system`: this is a PLM workspace people sit in
   * all day beside colour, image and swatch work, and it is designed and
   * reviewed light. Following the OS meant a laptop set to dark opened the app
   * in a theme nobody had checked those surfaces against.
   */
  theme: ThemeMode;
  /** Enable the ⌘K / Ctrl+K command palette. */
  commandPaletteEnabled: boolean;
  /** UI language. Pseudo-locale `en-XA` is dev-only. */
  locale: SupportedLocale;
  /**
   * Global UI density. Remaps control height, radius ladder, and chrome via
   * `data-density` on `<html>`. Default `normal` (36px controls / 8px corners).
   */
  density: AppDensity;
}

const defaults: UserPreferences = {
  showDashboardEditButton: true,
  theme: "light",
  commandPaletteEnabled: true,
  locale: SOURCE_LOCALE,
  density: DEFAULT_APP_DENSITY,
};

export const loadPreferences = (): UserPreferences => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { ...defaults };
    }

    const stored = JSON.parse(raw) as Record<string, unknown>;

    // Migrate legacy darkMode property
    if ("darkMode" in stored && !("theme" in stored)) {
      // `darkMode: false` meant "not dark", which is now spelled `light` — the
      // same value the default carries, rather than deferring to the OS.
      stored.theme = stored.darkMode === true ? "dark" : "light";
    }

    return {
      showDashboardEditButton:
        typeof stored.showDashboardEditButton === "boolean"
          ? stored.showDashboardEditButton
          : defaults.showDashboardEditButton,
      theme: VALID_THEMES.has(stored.theme as string)
        ? (stored.theme as ThemeMode)
        : defaults.theme,
      commandPaletteEnabled:
        typeof stored.commandPaletteEnabled === "boolean"
          ? stored.commandPaletteEnabled
          : defaults.commandPaletteEnabled,
      locale: isSupportedLocale(stored.locale) ? stored.locale : defaults.locale,
      density: isAppDensity(stored.density) ? stored.density : defaults.density,
    };
  } catch {
    return { ...defaults };
  }
};

export const savePreferences = (prefs: UserPreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable
  }
};
